<template>
  <div class="edit-web-property">
    <div class="edit-web-property__header">
      <div class="edit-web-property__title">{{editWebPropertyTitle}}</div>
    </div>

    <template>
      <div class="edit-web-property__type-and-status-selection">
        <div class="edit-web-property__type-and-status-selection_block">
          <div class="edit-web-property__field_label">Type</div>
          <base-select class="edit-web-property__select"
                       :class="{'edit-web-property__field_err': selectErrors.some(err => ['type', 'multiple-client-groups'].includes(err)), 'edit-web-property__fields_disabled': disableButtons}"
                       :options="typesOptions"
                       alignLeft
                       :isSmartInput="false"
                       :isSortAsc="true"
                       :disabled="disableButtons"
                       showTopBorder
                       shiftLabelRight
                       v-model="type"
                       :selectStyle="{height: '60px!important'}"
                       placeholder="Select Type"/>
        </div>
        <span class="edit-web-property__error-message" v-if="selectErrors.includes('multiple-client-groups')">{{ multipleClientsErrorMessage }}</span>
      </div>
      <div v-if="!isEditMode" class="edit-web-property__homepage_url">
        <div class="edit-web-property__field_label">Home Page URL</div>
        <input v-model="homepageUrl"
               :class="['edit-web-property__homepage_url_field', {'edit-web-property__field_err': selectErrors.includes('homepageUrl'), 'edit-web-property__fields_disabled': disableButtons}]"
               type="text" />
      </div>
      <div class="edit-web-property__group_name">
        <div class="edit-web-property__field_label">Group Name</div>
        <input v-model="groupName"
               :class="['edit-web-property__group_name_field', {'edit-web-property__field_err': selectErrors.includes('groupName'), 'edit-web-property__fields_disabled': disableButtons}]"
               type="text" />
      </div>
      <div class="edit-web-property__patterns">
        <div class="edit-web-property__field_label">Patterns</div>
        <div class="edit-web-property__field_sublabel">The % character is used for wild card matching in the system. It can be used anywhere in the web property pattern (beginning, middle or end)</div>
        <div class="edit-web-property__patterns_fields-wrapper">
          <div v-for="(item, index) in patterns" class="edit-web-property__patterns_field-wrapper" :key="index">
            <input v-model="patterns[index].value"
                   placeholder="Enter Pattern name…"
                   :class="['edit-web-property__patterns_field', {'edit-web-property__fields_disabled': disableButtons}]"
                   type="text"/>
            <base-material-design-icon class="edit-web-property__patterns_cross"
                                       icon="plus"
                                       v-if="!disableButtons"
                                       @click="removePattern(index)" />
          </div>
          <div @click="addNewPattern" :class="['edit-web-property__patterns_add-new-pattern', {'edit-web-property__fields_disabled': disableButtons}]">
            <base-material-design-icon class="edit-web-property__patterns_add-new-pattern_cross" icon="plus" />
            <span style="padding-left: 40px;">Add new pattern</span>
          </div>
        </div>
      </div>
    </template>

    <div class="edit-web-property__footer">
      <button class="edit-web-property__button edit-web-property__button_cancel"
              :class="{'edit-web-property__fields_disabled': disableButtons}"
              @click="close(false)">cancel</button>
      <button v-if="isEditMode"
              class="edit-web-property__button edit-web-property__button_delete"
              :class="{'edit-web-property__fields_disabled': disableButtons}"
              @click="openConfirmModal">delete</button>
      <button class="edit-web-property__button edit-web-property__button_add"
              :class="{'edit-web-property__fields_disabled': disableButtons}"
              @click="apply">save</button>
    </div>
    <modal name="confirm-modal" v-bind="modalProps">
      <base-confirm-modal title="Are you sure you want to delete this?"
                          subtitle="This Web Property Group will be removed permanently!"
                          @confirm="confirmModal" />
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import BaseModalMixins from '@/common/BaseModalMixins'
import validator from 'validator'

const patternTemplate = {
  value: '',
  id: -1
}

export default {
  name: 'EditWebPropertyModal',
  mixins: [BaseModalMixins],
  mounted () {
    if (this.isEditMode) {
      const { type, property_group_name, pattern } = this.editModalData
      this.patterns = pattern
      this.type = type
      this.groupName = property_group_name
    }
  },
  props: {
    editModalType: {
      type: String,
      required: true
    },
    editModalData: {
      type: Object,
      default: () => ({})
    },
    hasClientWebPropertyGroup: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectErrors: [],
      type: null,
      status: 'Yes',
      disableButtons: false,
      patterns: [{ ...patternTemplate }],
      groupName: '',
      homepageUrl: '',
      multipleClientsErrorMessage: 'You cannot have more than one Client Web Property Group'
    }
  },
  watch: {
    type (val) {
      if (this.hasMultipleClientGroups()) {
        this.selectErrors.push('multiple-client-groups')
      } else {
        const typeIndex = this.selectErrors.indexOf('multiple-client-groups')
        if (typeIndex > -1) {
          this.selectErrors.splice(typeIndex, 1)
        }
      }
    },
    homepageUrl () {
      this.selectErrors = []
      // if empty do nothing, no errors
      if (!this.homepageUrl.trim()) {
        this.groupName = ''
        this.patterns = [{ ...patternTemplate }]
      } else if (validator.isURL(this.homepageUrl)) {
        // strip optional http(s) and www. from url to give suggested group name
        const leftStrip = /^(https?:\/\/)?(www\.)?/
        const rightStrip = /[/?].*/
        const suggestedGroupName = this.homepageUrl.replace(leftStrip, '').replace(rightStrip, '')
        this.groupName = suggestedGroupName

        // append '%' to url to give suggested web property pattern
        let newPattern = this.homepageUrl
        if (newPattern[newPattern.length - 1] !== '/') {
          newPattern += '/'
        }
        newPattern += '%'
        this.patterns = [{
          id: -1,
          value: newPattern
        }]
      } else {
        this.selectErrors.push('homepageUrl')
        this.groupName = ''
        this.patterns = [{ ...patternTemplate }]
      }
    }
  },
  computed: {
    ...mapState('webProperty', ['typesOptions']),
    isEditMode () {
      return this.editModalType === 'edit'
    },
    editWebPropertyTitle () {
      return !this.isEditMode ? 'Create New Web Property Group' : 'Edit Web Property Group'
    },
    modalProps () {
      return { ...baseModalProps, height: 'auto', width: '510px' }
    }
  },
  methods: {
    ...mapActions('webProperty', ['createNewWebProperty', 'removeWebProperty', 'updateWebProperty']),
    close (update = true) {
      this.$emit('close', update)
    },
    openConfirmModal () {
      this.$modal.show('confirm-modal')
    },
    confirmModal (status) {
      this.$modal.hide('confirm-modal')
      if (status) {
        this.deletePropertyGroup()
      }
    },
    hasMultipleClientGroups () {
      const clientGroupIsSelected = this.editModalType === 'edit' && this.editModalData.type.toLowerCase() === 'clients'
      return !clientGroupIsSelected && this.hasClientWebPropertyGroup && this.type.toLowerCase() === 'clients'
    },
    async apply () {
      const { type, status, groupName, patterns } = this
      const selectErrors = []
      if (this.hasMultipleClientGroups()) {
        selectErrors.push('multiple-client-groups')
      }
      !type && selectErrors.push('type')
      !groupName.length && selectErrors.push('groupName')

      if (selectErrors.length) {
        this.selectErrors = selectErrors
        return
      }

      this.selectErrors = []
      this.disableButtons = true

      const payload = {
        webPropertyId: this.isEditMode ? this.editModalData._id : -1,
        clientId: this.clientId,
        group_name: groupName,
        patterns: JSON.stringify(this.isEditMode
          ? patterns.map(v => {
            v.value = v.value.trim()
            return v
          })
          : patterns.map(v => v.value.trim())),
        status,
        type
      }

      try {
        if (this.isEditMode) {
          await this.updateWebProperty(payload)
        } else {
          await this.createNewWebProperty(payload)
        }
        this.close()
      } catch (err) {
        this.disableButtons = false
      }
    },
    async deletePropertyGroup () {
      this.disableButtons = true

      try {
        await this.removeWebProperty({
          webPropertyId: this.editModalData._id
        })
        this.close()
      } catch (err) {
        this.disableButtons = false
      }
    },
    addNewPattern () {
      const patterns = [...this.patterns]
      patterns.push({ ...patternTemplate })
      this.patterns = patterns
    },
    removePattern (index) {
      const patterns = [...this.patterns]
      patterns.splice(index, 1)
      this.patterns = patterns
    }
  }
}
</script>

<style scoped lang="scss">
  .edit-web-property {
    padding: 40px;

    &__error-message {
        color: $rfx-red;
        font-size: 10px;
        margin-top: 5px;
      }

    .v--modal-overlay {
      background-color: transparent !important;
    }

    &__type-and-status-selection {
      display: flex;
      margin-bottom: 24px;
      flex-direction: column;
      &_block {
        flex: 1;
      }

    }

    &__field {
      &_err {
        border: 1px solid $rfx-light-red !important;
      }
    }

    &__field_sublabel {
      font-size: 12px;
      margin-bottom: 8px;
      color: #999;
    }

    &__field_label {
      font-size: 14px;
      margin-bottom: 8px;
      color: $rfx-dark-march;
      font-weight: bold;
    }

    &__title {
      padding-bottom: 30px;
      text-align: center;
      font-size: 24px;
      line-height: 32px;
      color: $rfx-dark-march;
      font-weight: bold;
    }

    &__footer {
      margin-top: 40px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    &__button {
      min-width: 100px;
      padding: 0 25px;
      height: 40px;
      font-size: 10px;
      font-weight: 900;
      cursor: pointer;
      text-transform: uppercase;
      border-radius: 8px;
      outline: none;
      border: none;

      &_cancel {
        color: $rfx-dark-march;
        margin-right: 15px;
      }

      &_add {
        color: #fff;
        background-color: $rfx-dark-march;
        margin-left: 15px;
      }

      &_delete {
        color: #fff;
        background-color: $rfx-red;
        margin: 0 15px;
      }
    }

    &__fields_disabled {
        pointer-events: none;
        opacity: .5;
    }

    &__group_name {
      display: flex;
      width: 100%;
      flex-direction: column;
      margin-bottom: 24px;

      &_field {
        max-width: -webkit-fill-available;
        height: 58px;
        border: 1px solid #e7e9eb;
        padding: 0 15px;
        font-size: 14px;
        border-radius: 4px;
        color: $rfx-dark-march;
        width: 100%;
      }
    }

    &__homepage_url {
      display: flex;
      width: 100%;
      flex-direction: column;
      margin-bottom: 24px;

      &_field {
        max-width: -webkit-fill-available;
        height: 58px;
        border: 1px solid #e7e9eb;
        padding: 0 15px;
        font-size: 14px;
        border-radius: 4px;
        color: $rfx-dark-march;
        width: 100%;
      }
    }

    &__patterns {
      width: 100%;
      margin-top: 24px;

      &_add-new-pattern {
        cursor: pointer;
        text-transform: uppercase;
        font-size: 14px;
        line-height: 32px;
        color: $rfx-dark-march;
        font-weight: bold;
        height: 52px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: rgba(1, 22, 39, .1) !important;
        }

        &_cross {
          font-size: 23px;
          position: absolute;
          margin-left: -148px;
        }
      }

      &_fields-wrapper {
        border: 1px solid #e7e9eb;
        border-radius: 4px;
        display: flex;
        width: 100%;
        flex-direction: column;
      }

      &_field {
        height: 58px;
        border: none;
        border-bottom: 1px solid #e7e9eb;
        padding: 0 15px;
        font-size: 14px;
        border-radius: 4px;
        color: $rfx-dark-march;
        max-width: -webkit-fill-available;
        width: 100%;
      }

      &_field-wrapper {
        position: relative;
      }

      &_cross {
        position: absolute;
        font-size: 23px;
        transform: rotate(45deg);
        right: 11px;
        top: 17px;
        cursor: pointer;
      }
    }

    &__no-data {
      font-size: 24px;
      font-weight: 600;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      color: $rfx-light-march;
      padding: 30px 0;
    }

    &::v-deep .select__header_shifted {
      padding-left: 15px;
    }
  }
</style>
